import React, {useMemo, useRef} from "react";
import {Player} from "@lottiefiles/react-lottie-player";
import src from '../../../../../assets/animations/lottie/loading.json'
import classnames from "classnames";
import {deepCopy} from "../../../../../core/services/utils/utils";
import {useTheme} from "@mui/material";


/**
 * Utility class for lottie animations.
 */
class LottieAnimationUtils {

    rawPrimaryColor;
    rawSecondaryColor;
    #primaryColor;
    #secondaryColor;
    #middleColor;

    /**
     * Creates a new LottieAnimationUtils.
     * @param primaryColor
     * @param secondaryColor
     */
    constructor(primaryColor, secondaryColor) {
        this.rawPrimaryColor = primaryColor;
        this.rawSecondaryColor = secondaryColor;
    }

    /**
     * Converts the given hex color to a normalized RGBA color.
     * @param _hex   The hex color to convert.
     * @returns     The normalized RGBA color.
     */
    hexToNormalizedRgba(_hex) {
        let hex = _hex.toString();
        if (hex.startsWith("#")) {
            hex = hex.slice(1);
        }
        if (hex.length === 8) {
            const alpha = parseInt(hex.slice(6, 8), 16) / 255; // Normalize alpha to [0, 1]
            hex = hex.slice(0, 6); // Remove the alpha component
            const bigint = parseInt(hex, 16);
            const r = (bigint >> 16) & 255;
            const g = (bigint >> 8) & 255;
            const b = bigint & 255;
            return [r / 255, g / 255, b / 255, alpha];
        } else {
            const bigint = parseInt(hex, 16);
            const r = (bigint >> 16) & 255;
            const g = (bigint >> 8) & 255;
            const b = bigint & 255;
            return [r / 255, g / 255, b / 255, 1];
        }
    }

    /**
     * Returns the normalized primary color.
     */
    get normalizedPrimaryColor() {
        if (this.#primaryColor) {
            return this.#primaryColor;
        }
        this.#primaryColor = this.hexToNormalizedRgba(this.rawPrimaryColor);
        return this.#primaryColor;
    }

    /**
     * Returns the normalized secondary color.
     */
    get normalizedSecondaryColor() {
        if (this.#secondaryColor) {
            return this.#secondaryColor;
        }
        this.#secondaryColor = this.hexToNormalizedRgba(this.rawSecondaryColor);
        return this.#secondaryColor;
    }

    /**
     * Returns the normalized middle color.
     */
    get normalizedMiddleColor() {
        if (this.#middleColor) {
            return this.#middleColor;
        }
        this.#middleColor = this.interpolateColor(this.normalizedPrimaryColor, this.normalizedSecondaryColor);
        return this.#middleColor;
    }

    /**
     * Interpolates the given colors.
     * @param color1        The first color.
     * @param color2        The second
     * @returns {number[]}  The interpolated color.
     */
    interpolateColor(color1, color2) {
        const r = (color1[0] + color2[0]) / 2;
        const g = (color1[1] + color2[1]) / 2;
        const b = (color1[2] + color2[2]) / 2;
        return [r, g, b, 1];
    }

    /**
     * Converts the color of the given json to the primary color.
     * @param json  The json to convert.
     * @returns     The converted json.
     */
    colorLoadingAnimation(json) {
        const result = deepCopy(json);
        result.layers.forEach((layer) => {
            if (layer.shapes) {
                layer.shapes.forEach(shape => {
                    if (shape.it) {
                        shape.it.forEach(item => {
                            if (item.ty === 'gs') { // Check for gradient stroke type
                                const stops = item.g.k.k;
                                // Update the gradient stops
                                stops[1] = this.normalizedPrimaryColor[0]; // Red
                                stops[2] = this.normalizedPrimaryColor[1]; // Green
                                stops[3] = this.normalizedPrimaryColor[2]; // Blue
                                stops[5] = this.normalizedMiddleColor[0]; // Red
                                stops[6] = this.normalizedMiddleColor[1]; // Green
                                stops[7] = this.normalizedMiddleColor[2]; // Blue
                                stops[9] = this.normalizedSecondaryColor[0]; // Red
                                stops[10] = this.normalizedSecondaryColor[1]; // Green
                                stops[11] = this.normalizedSecondaryColor[2]; // Blue
                            }
                        });
                    }
                });
            }
        });
        return result;
    }
}

const LoadingLottieAnimation = ({className = '', width = '100%', height = 'auto'}) => {
    const playerRef = useRef();
    const lottieRef = useRef();
    const theme = useTheme();

    const LoadingAnimationSrc = useMemo(() => new LottieAnimationUtils(
        theme.palette.primary.main,
        theme.palette.secondary.main
    ).colorLoadingAnimation(src), [theme.palette?.primary?.main, theme.palette?.secondary?.main]);

    /**
     * Fetches the value of the given dimension.
     *
     * * if the given [d] is a number, returns the pixelated value of the [d], otherwise returns [d]
     * @param {number | string} d
     * @return {string|*}
     */
    const getDimensionValue = (d) => {
        const num = Number(d);
        if (Number.isNaN(num)) {
            return d;
        }
        return `${num}px`;
    }

    const dimensions = useMemo(() => ({
        width: getDimensionValue(width),
        height: getDimensionValue(height),
    }), [width, height])

    return (
        <>
            <Player
                autoplay
                loop
                src={LoadingAnimationSrc}
                ref={playerRef}
                style={{...dimensions}}
                lottieRef={(ref) => (lottieRef.current = ref)}
                className={classnames(className)}
            />
        </>
    )
}

export default LoadingLottieAnimation;
